// src/pages/cadastros/Usuarios/EditarUsuario.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

import { db } from "../../../config/firebase";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";

import { NIVEIS, LABEL_NIVEIS as NIVEL_LABEL } from "../../../config/niveis";
import { AREAS, pode, nivelUsuario } from "../../../utils/permissoes";

function EditarUsuario() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();   // ID do usuário na coleção "usuarios"

  // estados do form
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [nivel, setNivel] = useState(NIVEIS.TECNICO);
  const [nivelAlvoOriginal, setNivelAlvoOriginal] = useState(null);
  const [corAgenda, setCorAgenda] = useState("#3b82f6");
  const [areas, setAreas] = useState({
    [AREAS.MANUTENCAO]: true,
    [AREAS.FINANCEIRO]: false,
  });
  const [grupoId, setGrupoId] = useState("");
  const [empresaBase, setEmpresaBase] = useState("");

  const [tipoAcesso, setTipoAcesso] = useState("GLOBAL");
  // Mantido só para compatibilidade com documentos antigos; a UI usa seleção por checklist.
  const [empresasTexto, setEmpresasTexto] = useState("");

  // Empresas do grupo para seleção (RESTRITO)
  const [empresasDisponiveis, setEmpresasDisponiveis] = useState([]);
  const [empresasSelecionadas, setEmpresasSelecionadas] = useState([]);
  const [buscaEmpresa, setBuscaEmpresa] = useState("");

  const empresasFiltradas = useMemo(() => {
    const t = (buscaEmpresa || "").trim().toLowerCase();
    if (!t) return empresasDisponiveis;
    return empresasDisponiveis.filter((e) => (e.nome || "").toLowerCase().includes(t));
  }, [buscaEmpresa, empresasDisponiveis]);

  function toggleEmpresa(emp) {
    setEmpresasSelecionadas((prev) => {
      const existe = prev.some((p) => p.id === emp.id);
      if (existe) return prev.filter((p) => p.id !== emp.id);
      return [...prev, { id: emp.id, nome: emp.nome }];
    });
  }


  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const opcoesNivelBase = [
    { value: NIVEIS.DASHBOARD, label: NIVEL_LABEL[NIVEIS.DASHBOARD] },
    { value: NIVEIS.KIOSK, label: NIVEL_LABEL[NIVEIS.KIOSK] },
    { value: NIVEIS.CLIENTE, label: NIVEL_LABEL[NIVEIS.CLIENTE] },
    { value: NIVEIS.TECNICO, label: NIVEL_LABEL[NIVEIS.TECNICO] },
    { value: NIVEIS.TECNICO_SENIOR, label: NIVEL_LABEL[NIVEIS.TECNICO_SENIOR] },
    { value: NIVEIS.ADMIN, label: NIVEL_LABEL[NIVEIS.ADMIN] },
    { value: NIVEIS.GESTOR, label: NIVEL_LABEL[NIVEIS.GESTOR] },
  ];

  const nivelEditor = nivelUsuario(usuarioLogado);
  const podeEditarUsuario = pode(usuarioLogado, "cadastros", "usuarios", "altera");

  const opcoesNivel = useMemo(() => {
    if (nivelEditor === NIVEIS.ADMIN) return opcoesNivelBase.filter((n) => n.value < NIVEIS.ADMIN);
    if (nivelEditor === NIVEIS.GESTOR) return opcoesNivelBase;
    return opcoesNivelBase.filter(() => false);
  }, [nivelEditor]);


  useEffect(() => {
    async function carregarUsuario() {
      try {
        setCarregando(true);
        const ref = doc(db, "usuarios", id);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setErro("Usuário não encontrado.");
          setCarregando(false);
          return;
        }

        const dados = snap.data();

        setNome(dados.nome || "");
        setEmail(dados.email || "");
        setTelefone(dados.telefone || "");
        setNivel(dados.nivel || NIVEIS.TECNICO);
        setNivelAlvoOriginal(dados.nivel || NIVEIS.TECNICO);
        setCorAgenda(dados.corAgenda || "#3b82f6");
        const a = dados.areas;
        if (a && typeof a === "object") {
          setAreas({
            [AREAS.MANUTENCAO]: !!a[AREAS.MANUTENCAO],
            [AREAS.FINANCEIRO]: !!a[AREAS.FINANCEIRO] || !!a.FERRAMENTARIA,
          });
        }
        setGrupoId(dados.grupoId || usuarioLogado?.grupoId || "");
        setEmpresaBase(dados.empresa || "");

        const globalAccess = dados.globalAccess ?? true;
        setTipoAcesso(globalAccess ? "GLOBAL" : "RESTRITO");
        const raw = Array.isArray(dados.empresasPermitidas) ? dados.empresasPermitidas : [];
        setEmpresasTexto(raw.map((e) => (typeof e === "string" ? e : e?.nome)).filter(Boolean).join(", "));
        setEmpresasSelecionadas(raw
          .map((e) => (typeof e === "string" ? null : (e && e.id ? { id: e.id, nome: e.nome } : null)))
          .filter(Boolean));

        setCarregando(false);
      } catch (e) {
        console.error(e);
        setErro("Erro ao carregar usuário.");
        setCarregando(false);
      }
    }

    if (id) carregarUsuario();
  }, [id, usuarioLogado]);

  // Carrega empresas do grupo para o modo RESTRITO (mesma UX do cadastro)
  useEffect(() => {
    async function carregarEmpresasDoGrupo() {
      if (!usuarioLogado?.grupoId) return;
      try {
        const ref = collection(db, "empresas");
        const q = query(ref, where("grupoId", "==", usuarioLogado.grupoId));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
        setEmpresasDisponiveis(lista);
      } catch (e) {
        console.warn("Não foi possível carregar empresas do grupo:", e);
      }
    }

    carregarEmpresasDoGrupo();
  }, [usuarioLogado?.grupoId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!podeEditarUsuario) {
      setErro("Você não tem permissão para editar usuários.");
      return;
    }

    if (nivelEditor === NIVEIS.ADMIN && (nivelAlvoOriginal ?? nivel) >= NIVEIS.ADMIN) {
      setErro("Administrador não pode editar usuários Administrador/Gestor.");
      return;
    }

    if (nivelEditor === NIVEIS.ADMIN && Number(nivel) >= NIVEIS.ADMIN) {
      setErro("Administrador só pode definir nível abaixo de Administrador (0–4).");
      return;
    }


    if (!nome.trim()) {
      setErro("Informe o nome do usuário.");
      return;
    }

    const temAlgumaArea = Object.values(areas || {}).some(Boolean);
    if (!temAlgumaArea) {
      setErro("Selecione pelo menos uma área de acesso.");
      return;
    }

    try {
      setSalvando(true);

      const globalAccess = tipoAcesso === "GLOBAL";
      const empresasPermitidas = globalAccess
        ? []
        : empresasSelecionadas
            .map((e) => String(e.id || "").trim())
            .filter(Boolean);

      const empresasPermitidasDetalhes = globalAccess
        ? []
        : empresasSelecionadas
            .map((e) => ({ id: String(e.id || "").trim(), nome: String(e.nome || "").trim() }))
            .filter((e) => e.id);

      // Define empresa padrão (para Cliente/Kiosk) quando o acesso é restrito a uma única empresa
      let empresaIdEfetivo = null;
      let empresaNomeEfetivo = String(empresaBase || "").trim();
      if (!globalAccess && empresasSelecionadas.length === 1) {
        const e0 = empresasSelecionadas[0] || {};
        empresaIdEfetivo = String(e0.id || "").trim() || null;
        empresaNomeEfetivo = String(e0.nome || "").trim() || empresaNomeEfetivo;
      }

      // Regra: Cliente/Kiosk/Dashboard precisam estar fixos em 1 empresa
      if (Number(nivel) <= NIVEIS.CLIENTE && !empresaIdEfetivo) {
        setErro("Para Cliente/Kiosk, selecione exatamente 1 empresa no acesso restrito.");
        return;
      }

      const ref = doc(db, "usuarios", id);
      await updateDoc(ref, {
        nome,
        telefone,
        nivel,
        grupoId,
        // Compatibilidade (nome)
        empresa: empresaNomeEfetivo || empresaBase,
        // Preferência (id + nome)
        empresaId: empresaIdEfetivo,
        empresaNome: empresaNomeEfetivo || empresaBase,
        globalAccess,
        tipoAcesso,
        empresasPermitidas,
        empresasPermitidasDetalhes,
        areas,
        corAgenda: (corAgenda || "#3b82f6").toLowerCase(),
        updatedAt: new Date(),
      });

      setSucesso("Usuário atualizado com sucesso!");
    } catch (e) {
      console.error(e);
      setErro("Erro ao salvar alterações.");
    } finally {
      setSalvando(false);
    }
  };

  if (!podeEditarUsuario) {
    return (
      <LayoutDesktop tema={tema} titulo="Editar Usuário" subtitulo="">
        <div style={{ padding: 16 }}>
          <div style={{ padding: 16, borderRadius: 12, background: "#111827", color: "#fff" }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>Acesso negado</h2>
            <p style={{ marginTop: 8, opacity: 0.9 }}>Seu perfil não tem permissão para editar usuários.</p>
            <button onClick={() => navigate(-1)} style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, border: "none", cursor: "pointer" }}>Voltar</button>
          </div>
        </div>
      </LayoutDesktop>
    );
  }

  if (carregando) {
    return (
      <LayoutDesktop tema={tema} titulo="Editar Usuário" subtitulo="">
        <p>Carregando dados...</p>
      </LayoutDesktop>
    );
  }


  if (nivelEditor === NIVEIS.ADMIN && (nivelAlvoOriginal ?? nivel) >= NIVEIS.ADMIN) {
    return (
      <LayoutDesktop tema={tema} titulo="Editar Usuário" subtitulo="">
        <div style={{ padding: 16 }}>
          <div style={{ padding: 16, borderRadius: 12, background: "#111827", color: "#fff" }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>Acesso negado</h2>
            <p style={{ marginTop: 8, opacity: 0.9 }}>Administrador não pode editar usuários Administrador/Gestor.</p>
            <button onClick={() => navigate(-1)} style={{ marginTop: 12, padding: "10px 14px", borderRadius: 10, border: "none", cursor: "pointer" }}>Voltar</button>
          </div>
        </div>
      </LayoutDesktop>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Editar Usuário"
      subtitulo={nome ? `Ajuste de dados do usuário ${nome}` : ""}
      usuario={usuarioLogado?.email || "Usuário Mamute"}
      empresa={usuarioLogado?.empresa || "Phoenix Injeção"}
    >
      <section className="pc-card">
        <h2 style={{ marginTop: 0 }}>Dados do usuário</h2>

        {erro && (
          <div className="pc-error-banner" style={{ marginTop: 12 }}>
            {erro}
          </div>
        )}

        {sucesso && (
          <div className="pc-success-banner" style={{ marginTop: 12 }}>
            {sucesso}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          style={{
            marginTop: 12,
            maxWidth: 650,
            marginLeft: "auto",
            marginRight: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {/* Nome */}
          <label>
            <span style={{ fontSize: 14 }}>Nome completo</span>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
              }}
            />
          </label>

          {/* E-mail (somente leitura) */}
          <label>
            <span style={{ fontSize: 14 }}>E-mail (não editável)</span>
            <input
              type="email"
              value={email}
              readOnly
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                background: "#02061755",
                cursor: "not-allowed",
              }}
            />
          </label>

          {/* Telefone */}
          <label>
            <span style={{ fontSize: 14 }}>Telefone</span>
            <input
              type="text"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
              }}
            />
          </label>

          {/* Nível */}
          <label>
            <span style={{ fontSize: 14 }}>Nível de acesso</span>
            <select
              value={nivel}
              onChange={(e) => setNivel(Number(e.target.value))}
              style={{
                width: "100%",
                padding: "9px 11px",
                marginTop: 4,
                borderRadius: 8,
                border: "1px solid #334155",
                
                color: "inherit",
              }}
            >
              {opcoesNivel.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          {/* COR NO CALENDARIO */}
          <label>
            <span style={{ fontSize: 14 }}>Cor no calendario</span>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
              <input
                type="color"
                value={corAgenda}
                onChange={(e) => setCorAgenda(e.target.value)}
                style={{
                  width: 48,
                  height: 34,
                  padding: 0,
                  borderRadius: 8,
                  border: "1px solid #334155",
                  background: "transparent",
                }}
              />
              <input
                type="text"
                value={corAgenda}
                onChange={(e) => setCorAgenda(e.target.value)}
                style={{
                  flex: 1,
                  padding: "9px 11px",
                  marginTop: 0,
                  borderRadius: 8,
                  border: "1px solid #334155",
                }}
              />
            </div>
          </label>

          {/* ÁREAS */}
          <div>
            <span style={{ fontSize: 14 }}>Áreas do sistema</span>
            <div
              style={{
                marginTop: 4,
                display: "flex",
                flexDirection: "column",
                gap: 6,
                fontSize: 14,
              }}
            >
              <label>
                <input
                  type="checkbox"
                  checked={!!areas[AREAS.MANUTENCAO]}
                  onChange={(e) =>
                    setAreas((prev) => ({ ...prev, [AREAS.MANUTENCAO]: e.target.checked }))
                  }
                  style={{ marginRight: 6 }}
                />
                Manutenção
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={!!areas[AREAS.FINANCEIRO]}
                  onChange={(e) =>
                    setAreas((prev) => ({ ...prev, [AREAS.FINANCEIRO]: e.target.checked }))
                  }
                  style={{ marginRight: 6 }}
                />
                Financeiro
              </label>
            </div>
          </div>

          {/* Empresa Base removida da tela de edição (mantida apenas internamente para compatibilidade) */}

          {/* Acesso (mesmo padrão do cadastro) */}
          <div>
            <span style={{ fontSize: 14 }}>Tipo de acesso</span>
            <div
              style={{
                marginTop: 4,
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontSize: 14,
              }}
            >
              <label>
                <input
                  type="radio"
                  name="tipoAcesso_edit"
                  value="GLOBAL"
                  checked={tipoAcesso === "GLOBAL"}
                  onChange={() => setTipoAcesso("GLOBAL")}
                  style={{ marginRight: 6 }}
                />
                Global (todas as empresas)
              </label>
              <label>
                <input
                  type="radio"
                  name="tipoAcesso_edit"
                  value="RESTRITO"
                  checked={tipoAcesso === "RESTRITO"}
                  onChange={() => setTipoAcesso("RESTRITO")}
                  style={{ marginRight: 6 }}
                />
                Restrito a empresas específicas
              </label>
            </div>

            {tipoAcesso === "RESTRITO" && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 14, marginBottom: 6 }}>
                  Empresas com acesso
                </div>

                <input
                  type="text"
                  value={buscaEmpresa}
                  onChange={(e) => setBuscaEmpresa(e.target.value)}
                  placeholder="Buscar empresa..."
                  style={{
                    width: "100%",
                    padding: 10,
                    borderRadius: 10,
                    border: "1px solid var(--cor-borda-input, rgba(255,255,255,0.12))",
                    background: "var(--cor-input-bg, var(--cor-card, rgba(255,255,255,0.04)))",
                    color: "var(--cor-input-texto, inherit)",
                    marginBottom: 10,
                  }}
                />

                <div
                  style={{
                    border: "1px solid var(--cor-borda-input, rgba(255,255,255,0.12))",
                    borderRadius: 12,
                    padding: 10,
                    maxHeight: 220,
                    overflow: "auto",
                    background: "var(--cor-card, rgba(255,255,255,0.04))",
                  }}
                >
                  {empresasFiltradas.length === 0 ? (
                    <div style={{ fontSize: 13, opacity: 0.7 }}>
                      Nenhuma empresa encontrada.
                    </div>
                  ) : (
                    empresasFiltradas.map((emp) => {
                      const checked = empresasSelecionadas.some((e) => e.id === emp.id);
                      return (
                        <label
                          key={emp.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "6px 4px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleEmpresa(emp)}
                          />
                          <span style={{ fontSize: 14 }}>{emp.nome}</span>
                        </label>
                      );
                    })
                  )}
                </div>

                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 8 }}>
                  Dica: no acesso restrito, marque apenas as empresas que esse usuário pode acessar.
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/cadastros/usuarios")}
              className="pc-btn-secundario"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="pc-btn-primario"
              disabled={salvando}
            >
              {salvando ? "Salvando..." : "Salvar alterações"}
            </button>
          </div>
        </form>
      </section>
    </LayoutDesktop>
  );
}

export default EditarUsuario;